import { useState } from "react";
import { FileText, Mail, Phone, CheckSquare, Users, HeartPulse } from "lucide-react";
import { T, TONES, MANROPE, ddmon } from "../clientsTokens";
import { Card, CardHead, Secondary, DotsMenu } from "./recordBits";

const TYPES = [
  { key: "all", label: "All" }, { key: "note", label: "Notes" }, { key: "email", label: "Emails" },
  { key: "call", label: "Calls" }, { key: "task", label: "Tasks" }, { key: "meeting", label: "Meetings" },
];
const ICON = { note: FileText, email: Mail, call: Phone, task: CheckSquare, meeting: Users, health: HeartPulse };
const PAGE = 7;

/* §23 — history, newest first. Type tiles are never tinted; zero-count chips are inert. */
export const ActivityCard = ({ items, loading, onDelete, onMakeTodo }) => {
  const [type, setType] = useState("all");
  const [shown, setShown] = useState(PAGE);

  const counts = TYPES.reduce((a, t) => ({
    ...a, [t.key]: t.key === "all" ? items.length : items.filter((i) => i.kind === t.key).length,
  }), {});
  const rows = type === "all" ? items : items.filter((i) => i.kind === type);
  const page = rows.slice(0, shown);

  return (
    <Card testid="client-activity">
      <div className="flex items-center gap-2.5 px-4 py-3 flex-wrap" style={{ borderBottom: `1px solid ${T.dividerInner}` }}>
        <span style={{ ...MANROPE, fontSize: 14, fontWeight: 700, color: T.ink }}>Activity</span>
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          {TYPES.map((t) => {
            const n = counts[t.key];
            const inert = n === 0 && t.key !== "all";
            const on = type === t.key;
            return (
              <button key={t.key} disabled={inert} data-testid={`activity-chip-${t.key}`}
                onClick={() => { setType(t.key); setShown(PAGE); }}
                className="h-7 px-2.5 rounded-full text-[12px] inline-flex items-center gap-1.5 whitespace-nowrap"
                style={{ background: on ? T.accentTint : T.card,
                  border: `1px solid ${on ? T.accentEdge : T.border}`,
                  color: inert ? T.ink3 : T.ink, fontWeight: on ? 700 : 500,
                  cursor: inert ? "default" : "pointer" }}>
                {t.label}<span className="mono text-[10.5px]" style={{ color: inert ? T.ink3 : T.ink2 }}>{n}</span>
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        [0, 1, 2].map((i) => (
          <div key={i} className="px-4 py-4 animate-pulse" style={{ borderBottom: `1px solid ${T.dividerRow}` }}>
            <div className="h-3 rounded" style={{ width: "40%", background: T.sunken }} />
          </div>
        ))
      ) : page.length === 0 ? (
        <div className="px-4 py-8 text-[13px] font-semibold" style={{ color: T.ink }}
          data-testid="activity-empty">
          {items.length === 0 ? "No activity recorded yet." : "No activity of this type."}
        </div>
      ) : page.map((it) => {
        const Icon = ICON[it.kind] || FileText;
        return (
          <div key={it.id} className="px-4 py-3.5" style={{ borderBottom: `1px solid ${T.dividerRow}` }}
            data-testid={`activity-row-${it.id}`}>
            <div className="flex items-start gap-3">
              <span className="rounded-[9px] flex items-center justify-center shrink-0"
                style={{ width: 30, height: 30, background: T.inset, border: `1px solid ${T.dividerInner}`, color: T.muted }}>
                <Icon className="w-[15px] h-[15px]" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="mono text-[10px] uppercase flex items-center gap-1.5" style={{ color: T.ink3, letterSpacing: "0.08em" }}>
                  {it.kind}
                  {it.source === "hubspot" && (
                    <span className="mono text-[9px] px-1 rounded" data-testid={`activity-hubspot-${it.id}`}
                      style={{ background: T.inset, border: `1px solid ${T.dividerInner}`, color: T.muted, letterSpacing: "0.06em" }}>
                      FROM HUBSPOT
                    </span>
                  )}
                </div>
                <div className="text-[13px] font-semibold" style={{ color: T.ink }}>{it.title}</div>
                {it.body && (
                  <div className="text-[13px] leading-relaxed mt-0.5 whitespace-pre-wrap"
                    style={{ color: T.ink2 }}>{String(it.body).slice(0, 420)}</div>
                )}
                {it.subtitle && <div className="text-[11.5px] mt-0.5" style={{ color: T.ink3 }}>{it.subtitle}</div>}
                {it.meta?.submission_id && (
                  <a href={`/health-checks/${it.meta.submission_id}`} className="text-[11.5px] font-semibold mt-0.5 inline-block"
                    style={{ color: T.accentInk }} data-testid={`activity-health-check-${it.id}`}>
                    Open the Health Check
                  </a>
                )}
                {it.kind === "meeting" && it.actionItems?.length > 0 && (
                  <div className="mt-2 rounded-[10px] p-2.5 space-y-1.5" data-testid={`meeting-actions-${it.srcId}`}
                    style={{ background: T.inset, border: `1px solid ${T.dividerInner}` }}>
                    <div className="mono text-[10px]" style={{ color: T.ink3, letterSpacing: "0.08em" }}>
                      ACTION ITEMS · {it.actionItems.filter((a) => a.done).length}/{it.actionItems.length} done
                    </div>
                    {it.actionItems.map((ai, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <span className="mt-[5px] rounded-full shrink-0"
                          style={{ width: 5, height: 5, background: ai.done ? TONES.success.dot : T.muted }} />
                        <span className="text-[12.5px] leading-[1.5] flex-1"
                          style={{ color: ai.done ? T.ink3 : T.ink2, textDecoration: ai.done ? "line-through" : "none" }}>
                          {ai.text}{ai.owner ? <span style={{ color: T.ink3 }}> · {ai.owner}</span> : null}
                        </span>
                        {ai.todo_id ? (
                          <span className="mono text-[10px] shrink-0 mt-0.5" style={{ color: TONES.success.dot }}
                            data-testid={`action-todo-made-${it.srcId}-${idx}`}>TO-DO ADDED</span>
                        ) : onMakeTodo && (
                          <button onClick={() => onMakeTodo(it.srcId, idx, ai)}
                            className="shrink-0 text-[11px] font-semibold hover:underline"
                            style={{ color: T.accentInk }} data-testid={`action-make-todo-${it.srcId}-${idx}`}>
                            Make to-do
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <span className="mono text-[11.5px] whitespace-nowrap shrink-0" style={{ color: T.ink2 }}>
                {ddmon(it.date) || "—"}
              </span>
              {onDelete && it.srcId && (
                <DotsMenu size={26} testid={`activity-menu-${it.id}`} items={[
                  { label: "Delete activity", danger: true, testid: `activity-delete-${it.id}`,
                    hint: "Removes it from this record's history. Cannot be undone.",
                    onClick: () => onDelete(it) },
                ]} />
              )}
            </div>
          </div>
        );
      })}

      <div className="flex items-center gap-3 px-4 py-2.5" style={{ background: T.panel, borderTop: `1px solid ${T.dividerInner}` }}>
        <span className="mono text-[11.5px]" style={{ color: T.ink2 }}>
          {Math.min(shown, rows.length)} of {rows.length} activity records
        </span>
        {shown < rows.length && (
          <div className="ml-auto">
            <Secondary onClick={() => setShown(shown + 12)} testid="activity-load-more">Load earlier activity</Secondary>
          </div>
        )}
      </div>
    </Card>
  );
};
